import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield,
  Lock,
  Eye,
  Database,
  UserCheck,
  FileText,
  Mail,
  Phone,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';

const Privacy = () => {
  const sections = [
    {
      title: "Information We Collect",
      icon: <Database size={24} />,
      content: [
        "Personal information such as name, email address, phone number, and business details",
        "Spa management data including customer records, appointments, and financial information",
        "Usage data and analytics to improve our services",
        "Device information and technical data for app functionality",
        "Communication records when you contact our support team"
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <UserCheck size={24} />,
      content: [
        "Provide and maintain our spa management services",
        "Process transactions and manage your account",
        "Send important service updates and notifications",
        "Improve our products and develop new features",
        "Provide customer support and technical assistance",
        "Comply with legal obligations and protect our rights"
      ]
    },
    {
      title: "Data Security",
      icon: <Shield size={24} />,
      content: [
        "We use industry-standard encryption to protect your data",
        "Regular security audits and vulnerability assessments",
        "Secure data centers with 99.9% uptime guarantee",
        "Access controls and authentication measures",
        "Regular backups to prevent data loss",
        "Employee training on data protection practices"
      ]
    },
    {
      title: "Data Sharing",
      icon: <Lock size={24} />,
      content: [
        "We do not sell your personal information to third parties",
        "Data may be shared with trusted service providers under strict agreements",
        "Information may be disclosed if required by law or legal process",
        "We may share aggregated, anonymized data for business analytics",
        "Your consent is required for any other data sharing purposes"
      ]
    },
    {
      title: "Your Rights",
      icon: <Eye size={24} />,
      content: [
        "Access and review your personal information",
        "Request corrections to inaccurate data",
        "Delete your account and associated data",
        "Export your data in a portable format",
        "Opt-out of marketing communications",
        "Withdraw consent for data processing"
      ]
    },
    {
      title: "Cookies and Tracking",
      icon: <FileText size={24} />,
      content: [
        "We use essential cookies for app functionality",
        "Analytics cookies to understand usage patterns",
        "You can control cookie settings in your browser",
        "Third-party services may use their own cookies",
        "We respect your privacy preferences and choices"
      ]
    }
  ];

  const contactInfo = [
    {
      icon: <Mail size={20} />,
      title: "Email Us",
      details: ["support@wellnessproapp.com"]
    },
    {
      icon: <Phone size={20} />,
      title: "Call Us",
      details: ["+91 9311252321", "Mon-Fri: 9 AM - 6 PM IST"]
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-slate-200">
        <div className="container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
                <Shield className="w-8 h-8" />
              </div>
            </div>
            <h1 className="text-large text-gradient mb-4">
              Privacy Policy
            </h1>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              Your privacy is important to us. This policy explains how Wellness Pro collects, uses, and protects your personal information.
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-sm text-gray-500">
              <span>Last updated:</span>
              <span className="font-semibold">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-padding bg-white">
        <div className="container max-w-4xl">
          <motion.div
            className="prose prose-lg max-w-none"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-8 mb-8 border border-primary-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                At Wellness Pro (Brand name of EZ Services), we are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
                spa management software and services.
              </p>
            </div>

            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-card border border-gray-200 hover:shadow-card-hover transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white">
                      {section.icon}
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                  </div>
                  
                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="container max-w-4xl">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes 
                outlined in this Privacy Policy. When you delete your account, we will delete your personal information, 
                except where we are required to retain it for legal, regulatory, or business purposes.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Account data: Retained while your account is active</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Transaction records: 7 years for tax and legal compliance</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Analytics data: 2 years in anonymized form</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. We ensure that such 
                transfers comply with applicable data protection laws and implement appropriate safeguards to protect your 
                personal information.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
                the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this 
                Privacy Policy periodically for any changes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container max-w-4xl">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-gradient mb-4">
              Questions About Privacy?
            </h2>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              If you have any questions about this Privacy Policy or our data practices, please don't hesitate to contact us.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mb-6">
                  <div className="w-8 h-8 text-white">
                    {info.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="text-gray-600 leading-relaxed">{detail}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Link 
              to="/contact" 
              className="btn btn-primary btn-large inline-flex items-center gap-2"
            >
              Contact Us
              <ArrowLeft className="rotate-180" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
